import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const TeamUploadModal = ({ isOpen, onClose, teamId, teamName, onSuccess }) => {
  const { user } = useAuth();
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('link'); // 'link' or 'document'
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!uploadName?.trim()) {
      setError('Upload name is required');
      return;
    }

    if (uploadType === 'link' && !linkUrl?.trim()) {
      setError('URL is required for link uploads');
      return;
    }

    if (uploadType === 'document' && !selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      let fileUrl = '';
      let fileName = '';
      let fileSize = 0;
      let fileType = '';

      if (uploadType === 'document') {
        // Upload file to storage
        const timestamp = Date.now();
        const sanitizedFileName = selectedFile?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = `${teamId}/${timestamp}_${sanitizedFileName}`;

        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('team-stage-files')
          ?.upload(filePath, selectedFile);

        if (uploadError) throw uploadError;

        fileUrl = uploadData?.path;
        fileName = selectedFile?.name;
        fileSize = selectedFile?.size;
        fileType = selectedFile?.type;
      } else {
        // For links, store the URL directly
        fileUrl = linkUrl;
        fileName = uploadName;
        fileType = 'link';
      }

      // Create database record
      const { data, error: dbError } = await supabase
        ?.from('team_stage_uploads')
        ?.insert({
          team_id: teamId,
          stage_id: null, // General team upload, not stage-specific
          uploaded_by: user?.id,
          file_url: fileUrl,
          notes: uploadName // Store the upload name in notes field
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;

      // Reset form
      setUploadName('');
      setUploadType('link');
      setLinkUrl('');
      setSelectedFile(null);

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error uploading:', err);
      setError(err?.message || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setUploadName('');
      setUploadType('link');
      setLinkUrl('');
      setSelectedFile(null);
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Upload Resource</h2>
              <p className="text-xs text-muted-foreground">{teamName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Upload Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Upload Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e?.target?.value)}
              placeholder="Enter a descriptive name"
              disabled={uploading}
            />
          </div>

          {/* Upload Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Upload Type <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="uploadType"
                  value="link"
                  checked={uploadType === 'link'}
                  onChange={(e) => setUploadType(e?.target?.value)}
                  disabled={uploading}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">Link</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="uploadType"
                  value="document"
                  checked={uploadType === 'document'}
                  onChange={(e) => setUploadType(e?.target?.value)}
                  disabled={uploading}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">Document</span>
              </label>
            </div>
          </div>

          {/* Conditional Input */}
          {uploadType === 'link' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                URL <span className="text-red-500">*</span>
              </label>
              <Input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e?.target?.value)}
                placeholder="https://example.com"
                disabled={uploading}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                File <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                <input
                  type="file"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Icon name="Upload" size={32} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {selectedFile ? selectedFile?.name : 'Click to select a file'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    PDF, DOC, DOCX, PPT, PPTX, Images (Max 50MB)
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5" />
              <p className="text-xs text-red-800">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={uploading}
              iconName={uploading ? 'Loader' : 'Upload'}
              iconPosition="left"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamUploadModal;